import React, { useEffect, useState } from "react";
import { AlertTriangle, ChevronRight, CircleDollarSign, CreditCard, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TableRowsSkeleton } from "../skeleton/Skeleton";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectMemberBalance,
} from "../../features/billing/billing-slice";
import {
  fetchMemberBalance,
} from "../../features/billing/slice-extensions/billing-slice-extension";
import { fetchContracts } from "../../features/contracts/slice-extensions/contracts-slice-extension";
import { fetchPaymentTypes } from "../../features/payment-types/slice-extensions/payment-types-slice-extension";
import { ContractType } from "../../features/contracts/models/ContractType";
import { PaymentStatus } from "../../features/payments/models/PaymentStatus";
import { formatMoney } from "../../features/billing/utils/money";
import { formatMediumDate } from "../../features/utils/displayFormat";
import { contractTypeLabel } from "../../features/payments/utils/paymentLabels";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { ROUTES } from "../../routes";
import ContractForm from "./ContractForm";
import FormModal from "./FormModal";
import PaymentForm from "./PaymentForm";
import MemberDirectDebitSection from "./MemberDirectDebitSection";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface MemberBillingSectionProps {
  memberId: string;
}

/**
 * The member detail's billing context: their contracts, payment standing, quick
 * payment creation (drop-in or against a contract), and the link into the full
 * member-billing page.
 */
const MemberBillingSection: React.FC<MemberBillingSectionProps> = ({ memberId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Billing is an Owner/Admin concern — plain Instructors never see this section.
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const balance = useAppSelector(selectMemberBalance(memberId));
  const [form, setForm] = useState<{ contractId?: string } | null>(null);
  const [showContractForm, setShowContractForm] = useState(false);

  useEffect(() => {
    if (!isOrgOwnerOrAdmin) {
      return;
    }
    dispatch(fetchMemberBalance(memberId));
    // The payment form needs the contract list + price catalog ready.
    dispatch(fetchContracts({ page: 1, pageSize: 100 }));
    dispatch(fetchPaymentTypes({ includeArchived: false }));
  }, [dispatch, memberId, isOrgOwnerOrAdmin]);

  if (!isOrgOwnerOrAdmin) {
    return null;
  }

  const overdueCount = balance?.recentPayments.filter(
    (p) => p.status === PaymentStatus.Overdue
  ).length ?? 0;

  // Remaining visits across the low-balance pack payments — the number the
  // instructor actually cares about ("sell a new pack soon").
  const lowBalanceVisitsLeft = balance
    ? balance.recentPayments
        .filter((p) => balance.lowBalancePackIds.includes(p.id))
        .reduce((sum, p) => sum + (p.remainingBalance ?? 0), 0)
    : 0;
  const hasLowBalance = (balance?.lowBalancePackIds.length ?? 0) > 0;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-grey-10 dark:text-grey-90">
          <CircleDollarSign size={20} className="text-grey-30 dark:text-grey-70" />
          {t("billing.memberSection.title")}
        </h3>
        <div className="flex gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => setForm({})}>
            <Plus size={16} />
            <span>{t("billing.actions.newPayment")}</span>
          </button>
          {/* No contract yet: the primary move is creating one, and the details
              page would be empty anyway. Once a contract exists, details take
              this spot and "new contract" moves below the contract rows. */}
          {balance && balance.contracts.length === 0 ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowContractForm(true)}
            >
              <Plus size={16} />
              <span>{t("billing.actions.newContract")}</span>
            </button>
          ) : (
            balance && (
              <Link
                to={ROUTES.MEMBER_BILLING.replace(":id", memberId)}
                className="btn btn-secondary"
              >
                <span>{t("billing.actions.billingDetails")}</span>
                <ChevronRight size={16} />
              </Link>
            )
          )}
        </div>
      </div>

      {!balance ? (
        <div className="mt-3"><TableRowsSkeleton rows={2} /></div>
      ) : (
        <div className="mt-3 space-y-2">
          {overdueCount > 0 && (
            <div className="flex items-center gap-2 rounded-xl bg-red-100 px-3 py-2 text-sm font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
              <AlertTriangle size={16} className="shrink-0" />
              <span>{t("billing.memberSection.overdue", { count: overdueCount })}</span>
            </div>
          )}
          {hasLowBalance && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary-95 px-3 py-2 text-sm font-medium text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80">
              <span className="flex items-center gap-2">
                <AlertTriangle size={16} className="shrink-0" />
                <span>
                  {lowBalanceVisitsLeft === 0
                    ? t("billing.memberSection.packUsedUp")
                    : t("billing.memberSection.lowPackBalance", { count: lowBalanceVisitsLeft })}
                </span>
              </span>
              {balance.contracts.some((c) => c.type === ContractType.VisitPack) && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold underline-offset-2 hover:underline"
                  onClick={() => setForm({
                    contractId: balance.contracts.find((c) => c.type === ContractType.VisitPack)?.id,
                  })}
                >
                  <CreditCard size={14} />
                  <span>{t("billing.memberSection.sellNewPack")}</span>
                </button>
              )}
            </div>
          )}
          {balance.contracts.length === 0 ? (
            <p className="text-sm text-grey-40 dark:text-grey-60">
              {t("billing.memberSection.noContracts")}
            </p>
          ) : (
            <>
            {balance.contracts.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-grey-90 px-3 py-2 dark:border-primary-20"
              >
                <div className="text-sm">
                  <p className="font-medium text-grey-10 dark:text-grey-90">
                    {contractTypeLabel(c.type, t)}
                    {c.type === ContractType.VisitPack && c.packSize
                      ? ` · ${t("billing.common.visitsCount", { count: c.packSize })}`
                      : ""}
                    {c.monthlyFeeAmountMinorUnits != null && c.monthlyFeeCurrency
                      ? ` · ${t("billing.common.perMonth", { amount: formatMoney(c.monthlyFeeAmountMinorUnits, c.monthlyFeeCurrency, getDisplayLocale()) })}`
                      : ""}
                  </p>
                  <p className="flex flex-wrap items-center gap-2 text-xs text-grey-30 dark:text-grey-70">
                    <span>
                      {t(`billing.contractStatus.${c.status}`, { defaultValue: c.status })}
                      {" · "}
                      {t("billing.common.since", { date: formatMediumDate(`${c.validFrom.slice(0, 10)}T00:00:00`, getDisplayLocale()) })}
                    </span>
                    {hasLowBalance && c.type === ContractType.VisitPack && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary-95 px-2 py-0.5 font-medium text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80">
                        <AlertTriangle size={11} />
                        {t("billing.common.lowBalance")}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-text"
                  onClick={() => setForm({ contractId: c.id })}
                >
                  <CreditCard size={16} />
                  <span>{t("billing.actions.addPayment")}</span>
                </button>
              </div>
            ))}
            {/* Always reachable — a second pack or a different contract starts here too. */}
            <button
              type="button"
              className="btn btn-text"
              onClick={() => setShowContractForm(true)}
            >
              <Plus size={16} />
              <span>{t("billing.actions.newContract")}</span>
            </button>
            </>
          )}
        </div>
      )}

      <MemberDirectDebitSection memberId={memberId} />

      {showContractForm && (
        <FormModal onClose={() => setShowContractForm(false)}>
          <ContractForm
            initialMemberId={memberId}
            onSaved={() => {
              setShowContractForm(false);
              dispatch(fetchMemberBalance(memberId));
              dispatch(fetchContracts({ page: 1, pageSize: 100 }));
            }}
            onClose={() => setShowContractForm(false)}
          />
        </FormModal>
      )}

      {form && (
        <FormModal onClose={() => setForm(null)}>
          <PaymentForm
            mode="standalone"
            initialMemberId={memberId}
            initialContractId={form.contractId}
            onSaved={() => { setForm(null); dispatch(fetchMemberBalance(memberId)); }}
            onClose={() => setForm(null)}
          />
        </FormModal>
      )}
    </div>
  );
};

export default MemberBillingSection;
